import Header from "@/atomic/atoms/Header";
import { PropsView } from "../Models/pix";
import Spinner from "@/atomic/atoms/Spinner";
import { useEffect, useMemo, useRef } from "react";
import Modal from "@/atomic/atoms/Modal";
import useI18n from "@/hooks/useI18n";
import { useThemeContext } from "@/contexts/ThemeContext";

export default function CheckoutPixView({
  pixCode,
  copyCode,
  isVisibleModal,
  toggleModal,
  share,
  plate,
  codeExpiration,
  total,
  isLoading,
  error,
  tryAgain,
  pixImage,
  goBack,
  goHome,
  timer,
  showPaymentErrorModal,
  togglePaymentErrorModal,
}: PropsView) {
  const { t } = useI18n();
  const { themeName, theme } = useThemeContext();
  const isZapay = themeName === 'zapay';
  const title = isZapay ? 'Pedágio' : t("pages:paymentPix:payment");

  const maxTimerRef = useRef<number>(0);

  useEffect(() => {
    if (timer && maxTimerRef.current === 0) {
      const parts = timer.split(':');
      if (parts.length === 2) {
        maxTimerRef.current = parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
      }
    }
  }, [timer]);

  const timerProgress = useMemo(() => {
    if (!timer || maxTimerRef.current === 0) return 0;
    const parts = timer.split(':');
    if (parts.length !== 2) return 0;
    const minutes = parseInt(parts[0], 10);
    const seconds = parseInt(parts[1], 10);
    const remaining = minutes * 60 + seconds;
    const elapsed = maxTimerRef.current - remaining;
    return Math.max(0, Math.min(1, elapsed / maxTimerRef.current));
  }, [timer]);

  const renderModal = () => (
    <div className="fixed inset-0 z-20 bg-black/50 flex items-end justify-center">
      <div className="w-full max-w-[480px] animate-slide-up">
        <div className="bg-white rounded-t-[16px] px-6 pb-6 flex flex-col items-center">
          <div className="w-11 h-1 bg-[#bfbfbf] rounded-full mt-3 mb-4" />

          {!isZapay && (
            <div className="w-[62px] h-[62px] bg-[#e5f9f2] rounded-full flex items-center justify-center mb-6">
              <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none">
                <path d="M5 13l4 4L19 7" stroke="#10b981" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          )}

          <p
            className="text-[18px] font-bold text-[#0e0d0d] text-center tracking-[-0.1px] leading-[21.6px] mb-6"
            style={{ fontFamily: theme.fonts.primary }}
          >
            {t("pages:paymentPix:codeCopied")}
          </p>

          <div className="w-full h-px bg-[#eee] mb-6" />

          <p
            className="text-sm text-[#696966] text-center leading-[18.2px] mb-6"
            style={{ fontFamily: theme.fonts.primary }}
          >
            {t("pages:paymentPix:modalWarning")}
          </p>

          <button
            type="button"
            onClick={toggleModal}
            className="w-full h-[60px] rounded-[12px] text-white text-base font-bold tracking-[-0.1px]"
            style={{
              backgroundColor: theme.colors.primary,
              fontFamily: theme.fonts.primary
            }}
          >
            {t("pages:paymentPix:gotIt")}
          </button>
        </div>
      </div>
    </div>
  );

  const renderDefaultContent = () => (
    <div className="flex flex-col gap-5">
      <h2
        className="text-[22px] font-bold tracking-[-0.2px] leading-[26.4px]"
        style={{ color: '#363636', fontFamily: theme.fonts.primary }}
      >
        {t("pages:paymentPix:title")}
      </h2>

      {/* Summary Card */}
      <div
        className="bg-[#f9f9f9] border border-[#e6e6e6] rounded-[16px] p-5 flex flex-col gap-4"
        style={{ fontFamily: theme.fonts.secondary }}
      >
        <div className="flex items-center justify-between h-[22px]">
          <p className="text-base font-medium text-[#0e0d0d] tracking-[-0.4px] leading-[20.8px]">
            {t("pages:paymentPix:payment")}
          </p>
          <p className="text-sm text-[#696966] tracking-[-0.4px] leading-[18.2px]">
            {t("pages:paymentPix:plate")} : <span className="uppercase">{plate}</span>
          </p>
        </div>

        <div className="h-px bg-[#e6e6e6]" />

        <div className="flex flex-col gap-3.5">
          <div className="flex items-center justify-between h-5">
            <p className="text-sm text-[#696966] tracking-[-0.4px] leading-[18.2px]">
              {t("pages:paymentPix:passages")}
            </p>
            <p className="text-sm font-medium text-[#0e0d0d] tracking-[-0.4px] leading-[18.2px] text-right">
              {total}
            </p>
          </div>
          <div className="flex items-center justify-between h-5">
            <p className="text-sm text-[#696966] tracking-[-0.4px] leading-[18.2px]">
              {t("pages:paymentPix:finesInterest")}
            </p>
            <p className="text-sm font-medium text-[#0e0d0d] tracking-[-0.4px] leading-[18.2px] text-right">
              R$ 0,00
            </p>
          </div>
        </div>

        <div className="h-px bg-[#e6e6e6]" />

        <div className="flex items-center justify-between h-[22px]">
          <p className="text-base font-medium text-[#0e0d0d] tracking-[-0.4px] leading-[20.8px]">
            {t("pages:paymentPix:totalLabel")}
          </p>
          <p className="text-base font-medium text-[#0e0d0d] tracking-[-0.4px] leading-[20.8px] text-right">
            {total}
          </p>
        </div>
      </div>

      {/* Timer & Pix Code Card */}
      <div
        className="bg-white border border-[#e6e6e6] rounded-[16px] overflow-hidden flex flex-col"
        style={{ fontFamily: theme.fonts.secondary }}
      >
        <div className="pt-5 px-4 flex flex-col gap-5 items-center">
          {timer && (
            <div className="flex flex-col items-center gap-2 w-full">
              <p className="text-[28px] font-medium text-[#0e0d0d] tracking-[-0.4px] leading-[36.4px]">
                {timer}
              </p>
              <p className="text-sm text-[#696966] tracking-[-0.4px] leading-[18.2px]">
                {t("pages:paymentPix:pixExpiresIn")}
              </p>
            </div>
          )}

          {timer && (
            <div className="w-full h-[6px] bg-[#f3f3f3] rounded-[50px]">
              <div
                className="h-[6px] rounded-[50px] transition-all duration-1000"
                style={{
                  width: `${Math.max(1, timerProgress * 100)}%`,
                  backgroundColor: theme.colors.primary
                }}
              />
            </div>
          )}

          <div className="flex items-center justify-between w-full">
            <p className="text-base text-[#696966] tracking-[-0.4px] leading-[20.8px]">
              {t("pages:paymentPix:totalToPay")}
            </p>
            <p className="text-base font-medium text-[#0e0d0d] tracking-[-0.4px] leading-[20.8px] text-right">
              {total}
            </p>
          </div>
        </div>

        <div className="mx-4 h-px bg-[#e6e6e6] mt-5" />

        <div className="px-4 pt-5 pb-4 flex flex-col gap-5 items-center">
          <div className="text-center text-sm tracking-[-0.4px] leading-[18.2px] w-full">
            <span className="font-medium text-[#0e0d0d]">{t("pages:paymentPix:copyCodeBold")}</span>
            {' '}
            <span className="text-[#696966]">{t("pages:paymentPix:andPayInApp")}</span>
          </div>

          <button
            type="button"
            onClick={copyCode}
            className="bg-[#f3f3f3] rounded-[12px] p-3 w-full flex items-center gap-2 overflow-hidden"
          >
            <p className="flex-1 min-w-0 text-xs text-[#525252] tracking-[-0.4px] break-all leading-[16.8px] text-left">
              {pixCode}
            </p>
            <svg width={20} height={20} viewBox="0 0 20 20" fill="none" className="shrink-0">
              <rect x="7" y="7" width="9.5" height="9.5" rx="2" stroke="#525252" strokeWidth="1.5" />
              <path d="M13 7V5.5C13 4.39543 12.1046 3.5 11 3.5H5.5C4.39543 3.5 3.5 4.39543 3.5 5.5V11C3.5 12.1046 4.39543 13 5.5 13H7" stroke="#525252" strokeWidth="1.5" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  const renderZapayContent = () => (
    <div className="flex w-full flex-col">
      <h2 className="text-2xl font-bold" style={{ color: theme.colors.text, fontFamily: theme.fonts.primary }}>
        {t("pages:paymentPix:title")}
      </h2>
      <div className="mt-3 bg-white rounded-lg overflow-hidden">
        <div className="w-full p-4">
          <p className="text-xl font-bold text-gray-800 mb-3" style={{ fontFamily: theme.fonts.primary }}>
            {t("pages:paymentPix:resume")}
          </p>
          <div className="mt-3 flex w-full">
            <p className="text-sm text-gray-600 w-1/2">{t("pages:paymentPix:plate")}</p>
            <p className="text-sm font-medium w-1/2 uppercase text-right" style={{ color: theme.colors.text }}>{plate}</p>
          </div>
          <div className="mt-3 flex w-full">
            <p className="text-sm text-gray-600 w-1/2">{t("pages:paymentPix:value")}</p>
            <p className="text-sm font-medium w-1/2 text-right" style={{ color: theme.colors.text }}>{total}</p>
          </div>
          <div className="mt-3 flex w-full">
            <p className="text-sm text-gray-600 w-1/2">{t("pages:paymentPix:expiration")}</p>
            <p className="text-sm font-medium w-1/2 text-right" style={{ color: theme.colors.text }}>{codeExpiration}</p>
          </div>
        </div>
        {!isLoading && (
          <div className="flex w-full p-4 bg-[#E6E6E6] border-t border-gray-200">
            <p className="text-base font-semibold text-gray-700 w-1/2">{t("pages:paymentPix:orderValue")}</p>
            <p className="text-2xl font-bold text-gray-800 w-1/2 text-right">{total}</p>
          </div>
        )}
      </div>

      {timer && (
        <div className="flex justify-center items-center mt-4 mb-4 text-lg">
          <span className="text-base font-medium">{t("pages:paymentPix:paymentProcess")}&nbsp;</span>
          <span className="text-lg font-mono font-bold" style={{ color: theme.colors.text }}>{timer}</span>
        </div>
      )}

      {pixImage && (
        <div className="mt-3 w-full flex justify-center">
          <img
            src={`data:${pixImage?.type};base64, ${pixImage?.image}`}
            alt="Código pix"
            className="w-40 max-h-40"
          />
        </div>
      )}

      <p className="mt-5 text-sm">{t("pages:paymentPix:description")}</p>
      <div className="rounded-lg bg-gray-200 p-3 mt-2 w-full">
        <p className="break-words text-sm">{pixCode}</p>
      </div>
    </div>
  );

  return (
    <div className="h-full relative">
      <Header
        title={title}
        hasLeftButton
        goBack={goBack}
      />

      <main className="w-full flex justify-center h-[calc(100%-72px)] bg-white">
        <article className="h-full flex flex-col w-full max-w-[480px]">
          <div className="flex-1 overflow-y-auto px-4 pb-4 pt-8">
            {isZapay ? renderZapayContent() : renderDefaultContent()}
          </div>

          <div className="px-4 pb-4 pt-2">
            {isZapay && (
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={copyCode}
                  className="border p-2 h-[60px] w-full flex items-center justify-center"
                  style={{
                    backgroundColor: theme.colors.primary,
                    borderRadius: theme.borderRadius.medium,
                    fontFamily: theme.fonts.primary
                  }}
                >
                  <img src="/svg/icon-copy-white.svg" width={20} height={20} alt="ícone copiar" />
                  <span className="ml-2 text-white text-base">{t("pages:paymentPix:buttonCopy")}</span>
                </button>
                <button
                  type="button"
                  onClick={share}
                  className="bg-transparent border p-2 h-[60px] w-full flex items-center justify-center"
                  style={{
                    borderColor: theme.colors.textSecondary,
                    borderRadius: theme.borderRadius.medium,
                    color: theme.colors.textSecondary,
                    fontFamily: theme.fonts.primary
                  }}
                >
                  <svg
                    width={20}
                    height={20}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ color: theme.colors.textSecondary }}
                  >
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                  </svg>
                  <span className="ml-2 text-base" style={{ color: theme.colors.textSecondary }}>
                    {t("pages:paymentPix:buttonShare")}
                  </span>
                </button>
              </div>
            )}
            {!isZapay && (
              <div className="flex flex-col gap-5">
                <button
                  type="button"
                  onClick={copyCode}
                  className="w-full h-[60px] rounded-[12px] flex items-center justify-center gap-2 text-white text-base font-bold tracking-[-0.1px]"
                  style={{
                    backgroundColor: theme.colors.primary,
                    fontFamily: theme.fonts.primary
                  }}
                >
                  <img src="/svg/icon-copy-white.svg" width={20} height={20} alt="" />
                  {t("pages:paymentPix:buttonCopy")}
                </button>
                <button
                  type="button"
                  onClick={share}
                  className="w-full h-[60px] rounded-[12px] border border-[#0e0d0d] flex items-center justify-center gap-2 text-[#363636] text-base font-bold tracking-[-0.1px] bg-transparent"
                  style={{ fontFamily: theme.fonts.primary }}
                >
                  <svg
                    width={20}
                    height={20}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                  </svg>
                  {t("pages:paymentPix:buttonShare")}
                </button>
              </div>
            )}
          </div>
        </article>
      </main>

      {isVisibleModal && renderModal()}
      {isLoading && <Spinner />}

      {error && (
        <Modal
          title={t("pages:paymentPix:errorDescription")}
          message={t("pages:paymentPix:clickButton")}
          action={tryAgain}
          actionMessage={t("pages:paymentPix:tryAgain")}
          hasCloseButton={true}
          actionClose={goBack}
          actionClassName="w-full h-[60px] rounded-[12px] text-white text-base font-bold tracking-[-0.1px]"
          actionStyle={{
            backgroundColor: theme.colors.primary,
            fontFamily: theme.fonts.primary
          }}
        />
      )}

      {showPaymentErrorModal && (
        <Modal
          title={t("pages:checkout:errors:processingFailedTitle")}
          message={t("pages:checkout:errors:processingFailedMessage")}
          action={() => {
            togglePaymentErrorModal();
            goBack();
          }}
          actionMessage={t("common:back")}
          hasCloseButton={true}
          actionClose={() => {
            togglePaymentErrorModal();
            goBack();
          }}
          actionClassName="w-full h-[60px] rounded-[12px] text-white text-base font-bold tracking-[-0.1px]"
          actionStyle={{
            backgroundColor: theme.colors.primary,
            fontFamily: theme.fonts.primary
          }}
        />
      )}
    </div>
  );
}
